import { GetApp, Delete as DeleteIcon } from "@mui/icons-material";
import {
  TableContainer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import Component from "components/Component";
import Button from "components/common/Button";
import DefaultLoader from "components/common/DefaultLoader";
import IconButton from "components/common/IconButton";
import DownloadOriginalButton from "components/common/files/DownloadOriginalButton";
import { useDispatch, useSelector } from "react-redux";
import * as filesService from "services/filesService";
import * as tablesService from "services/tablesService";
import * as hazardActions from "store/actions/input/exposure/hazardActions";
import * as exposureSelectors from "store/selectors/input/exposure/exposureSelectors";
import * as programSelectors from "store/selectors/input/program/programSelectors";

const DownloadParquetFilesButton = ({
  fileIds,
  columnMap,
  fileName,
  sheetName,
  isDisabled,
}) => {
  const [
    triggerDownload,
    { isLoading, isError },
  ] = filesService.useDownloadExcelTableMutation();

  return (
    <Button
      onClick={() =>
        triggerDownload({ fileIds, sheetName, columnMap, fileName })
      }
      startIcon={<GetApp />}
      isLoading={isLoading}
      isDisabled={isDisabled}
      hasError={isError}
    >
      {"Download"}
    </Button>
  );
};

const FileRow = ({
  fileId,
  insured,
  hideFile,
  parquetColumnMap,
  outputFilePrefix,
}) => {
  const {
    data: metadata,
    status: metadataStatus,
  } = tablesService.useGetTransformMetadataQuery(fileId, {
    skip: !fileId,
  });

  const originalFileId = metadata?.mapping?.id ?? "";
  const originalSheetName = metadata?.mapping?.tableName ?? "";

  const {
    data: sourceMetadata,
    status: sourceMetadataStatus,
  } = filesService.useGetFileMetadataQuery(originalFileId, {
    skip: !originalFileId,
  });

  const originalFilename = sourceMetadata?.originalFilename ?? "";

  const loadingMetadata = metadataStatus !== "fulfilled";
  const loading = loadingMetadata || sourceMetadataStatus !== "fulfilled";

  return (
    <TableRow>
      <TableCell padding={"checkbox"}>
        <IconButton icon={DeleteIcon} onClick={() => hideFile(fileId)} />
      </TableCell>
      <TableCell>{loading ? <DefaultLoader /> : originalFilename}</TableCell>
      <TableCell>
        {loadingMetadata ? <DefaultLoader /> : originalSheetName}
      </TableCell>
      <TableCell>
        <DownloadParquetFilesButton
          fileIds={[fileId]}
          columnMap={parquetColumnMap}
          fileName={`${outputFilePrefix}-${insured}-${originalFilename}-${originalSheetName}.xlsx`}
          sheetName={originalSheetName}
          isDisabled={loadingMetadata}
        />
      </TableCell>
      <TableCell>
        <DownloadOriginalButton
          {...{
            isDisabled: loading,
            sourceMetadata,
          }}
        />
      </TableCell>
      <TableCell />
    </TableRow>
  );
};

const SicCodesDownloader = () => {
  const dispatch = useDispatch();

  const hideFile = (fileId) => {
    dispatch(hazardActions.hideHazardFile(fileId));
  };

  const files = useSelector(exposureSelectors.selectHazardProfileFiles);
  const insured = useSelector(programSelectors.getInsured);
  const title = "Download SIC Codes Files";
  const parquetColumnMap = {
    _STATE: "State",
    _SIC: "SIC Code",
    _PREMIUM: "Premium",
  };
  const outputFilePrefix = "SIC-Codes";

  return (
    <Component title={title} sm_width={12} md_width={8} growVertically>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding={"checkbox"} />
              <Tooltip title={"The filename of the file that was uploaded."}>
                <TableCell>{"Filename"}</TableCell>
              </Tooltip>
              <Tooltip title={"The sheet of the file that was uploaded."}>
                <TableCell>{"Sheet"}</TableCell>
              </Tooltip>
              <Tooltip
                title={
                  "The file after it has been mapped and how it is used for pricing."
                }
              >
                <TableCell>{"Transformed"}</TableCell>
              </Tooltip>
              <Tooltip title={"The original file that was uploaded."}>
                <TableCell>{"Original"}</TableCell>
              </Tooltip>
              <TableCell></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {files.map((fileId) => (
              <FileRow
                {...{
                  key: fileId,
                  fileId,
                  insured,
                  hideFile,
                  parquetColumnMap,
                  outputFilePrefix,
                }}
              />
            ))}
            <TableRow>
              <TableCell />
              <TableCell />
              <TableCell colSpan={4}>
                <DownloadParquetFilesButton
                  fileIds={files}
                  columnMap={parquetColumnMap}
                  fileName={`All-${outputFilePrefix}-${insured}.xlsx`}
                  sheetName={outputFilePrefix}
                >
                  <GetApp />
                  {"Download All"}
                </DownloadParquetFilesButton>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Component>
  );
};

export default SicCodesDownloader;
